"use client";

import { Microphone } from "@/types";
import FilterSortPanel from "@components/collection/filters";
import MicrophoneList from "@components/collection/microphone-list";
import AlphabetNav from "@components/collection/alphabet-nav";
import React, { useRef } from "react";
import { useState, useCallback } from "react";
import SlideToggle from "@components/collection/slide-toggle";
import { TfiViewGrid, TfiViewList } from "react-icons/tfi";
import gsap from "gsap";
import { useReducedMotion } from "@/hooks/useReducedMotion";

interface CollectionContainerProps {
  microphones: Microphone[];
  totalCount: number;
}

const CollectionContainer = ({
  microphones,
  totalCount,
}: CollectionContainerProps) => {
  // Sort microphones by name initially
  const initialSortedMicrophones = [...microphones].sort((a, b) => {
    const nameA = `${a.manufacturer} ${a.model}`.toLowerCase();
    const nameB = `${b.manufacturer} ${b.model}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const [filteredList, setFilteredList] = useState<Microphone[]>(
    initialSortedMicrophones,
  );
  const [listOrCard, setListOrCard] = useState("card");
  const [showFilters, setShowFilters] = useState(false);
  const [microphoneRegistry, setMicrophoneRegistry] = useState<
    Map<string, HTMLElement>
  >(new Map());

  // Refs for filter button animation
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const topLettersRef = useRef<(HTMLSpanElement | null)[]>([]);
  const bottomLettersRef = useRef<(HTMLSpanElement | null)[]>([]);
  const arrowRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();

  // Filter button text based on state
  const filterButtonText = showFilters
    ? "HIDE SEARCH & FILTERS"
    : "SEARCH & FILTERS";
  const filterLetters = filterButtonText.split("");

  // field-gothic's ampersand reads as an 8, so that glyph alone borrows the body face
  const letterStyle = (char: string): React.CSSProperties =>
    char === "&"
      ? {
          fontFamily: "var(--font-body)",
          fontSize: "0.85em",
          padding: "0 0.06em",
          position: "relative",
          top: "0.06em",
        }
      : { whiteSpace: char === " " ? "pre" : "normal" };

  const slideToggleArray = [
    { icon: <TfiViewGrid />, value: "card" },
    { icon: <TfiViewList />, value: "list" },
  ];

  function handleSlideToggleClick(index: number) {
    setListOrCard(slideToggleArray[index].value);
  }

  // Function to update microphones
  const updateMicrophones = useCallback((updatedMicrophones: Microphone[]) => {
    setFilteredList(updatedMicrophones);
  }, []);

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Filter button hover animations (matching CTA button style)
  const handleFilterMouseEnter = () => {
    if (prefersReducedMotion) return;

    gsap.killTweensOf([topLettersRef.current, bottomLettersRef.current]);
    const tl = gsap.timeline();

    tl.to(
      topLettersRef.current,
      {
        yPercent: -100,
        duration: 0.35,
        ease: "power2.inOut",
        stagger: 0.015,
      },
      0,
    );

    tl.to(
      bottomLettersRef.current,
      {
        yPercent: -100,
        duration: 0.35,
        ease: "power2.inOut",
        stagger: 0.015,
      },
      0,
    );
  };

  const handleFilterMouseLeave = () => {
    if (prefersReducedMotion) return;

    gsap.killTweensOf([topLettersRef.current, bottomLettersRef.current]);
    const tl = gsap.timeline();

    tl.to(
      topLettersRef.current,
      {
        yPercent: 0,
        duration: 0.35,
        ease: "power2.inOut",
        stagger: 0.01,
      },
      0,
    );

    tl.to(
      bottomLettersRef.current,
      {
        yPercent: 0,
        duration: 0.35,
        ease: "power2.inOut",
        stagger: 0.01,
      },
      0,
    );
  };

  // Button press animation (scale to 0.97 per Emil Kowalski guidelines)
  const handleFilterMouseDown = () => {
    if (prefersReducedMotion) return;
    gsap.to(filterButtonRef.current, {
      scale: 0.97,
      duration: 0.1,
      ease: "power2.out",
    });
  };

  const handleFilterMouseUp = () => {
    if (prefersReducedMotion) return;
    gsap.to(filterButtonRef.current, {
      scale: 1,
      duration: 0.2,
      ease: "power2.out",
    });
  };

  // Function to register microphone elements for alphabet navigation
  const onMicrophoneRegister = useCallback(
    (id: string, letter: string, element: HTMLElement) => {
      setMicrophoneRegistry((prev) => {
        const newMap = new Map(prev);
        newMap.set(id, element);
        return newMap;
      });
    },
    [],
  );

  // Function to handle alphabet letter clicks
  const handleLetterClick = useCallback(
    (letter: string) => {
      // Find first microphone starting with the clicked letter
      const targetMic = filteredList.find((mic) => {
        const firstLetter = `${mic.manufacturer} ${mic.model}`
          .charAt(0)
          .toUpperCase();
        return firstLetter === letter;
      });

      if (targetMic) {
        const elementId = `mic-${letter}-${targetMic.slug}`;
        const element = microphoneRegistry.get(elementId);
        if (element) {
          // Use window.scrollTo instead of scrollIntoView to avoid layout issues
          const rect = element.getBoundingClientRect();
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
          const targetY = rect.top + scrollTop - 100; // 100px offset from top

          window.scrollTo({
            top: targetY,
            behavior: "smooth",
          });
        }
      }
    },
    [filteredList, microphoneRegistry],
  );

  return (
    <div className="collection-container relative w-full max-w-container mx-auto py-8 px-[10px] md:px-[15px] pr-20 md:pr-24">
      {/* Alphabet Navigation - positioned at container level */}
      <AlphabetNav
        microphones={filteredList}
        onLetterClick={handleLetterClick}
      />

      {/* Top Controls */}
      <div className="flex justify-between items-center mb-6">
        <button
          ref={filterButtonRef}
          onClick={toggleFilters}
          onMouseEnter={handleFilterMouseEnter}
          onMouseLeave={handleFilterMouseLeave}
          onMouseDown={handleFilterMouseDown}
          onMouseUp={handleFilterMouseUp}
          className="cta-button group inline-flex items-center justify-center overflow-hidden cursor-pointer"
          style={{ padding: "8px 16px" }}
        >
          {/* Text with letter animation */}
          <div className="relative overflow-hidden h-[1em] leading-none flex items-center">
            {/* Layer 1 */}
            <div className="flex">
              {filterLetters.map((char, i) => (
                <span
                  key={`top-${i}-${filterButtonText}`}
                  ref={(el) => {
                    topLettersRef.current[i] = el;
                  }}
                  className="inline-block"
                  style={letterStyle(char)}
                >
                  {char}
                </span>
              ))}
            </div>

            {/* Layer 2 - Positioned exactly at the bottom of the mask */}
            <div className="flex absolute top-full left-0">
              {filterLetters.map((char, i) => (
                <span
                  key={`bottom-${i}-${filterButtonText}`}
                  ref={(el) => {
                    bottomLettersRef.current[i] = el;
                  }}
                  className="inline-block"
                  style={letterStyle(char)}
                >
                  {char}
                </span>
              ))}
            </div>
          </div>

          {/* Arrow icon */}
          <div
            ref={arrowRef}
            className={`ml-0.5 transition-transform duration-200 ${showFilters ? "rotate-180" : ""}`}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </button>
        <SlideToggle
          buttonArray={slideToggleArray}
          handleClick={handleSlideToggleClick}
          currentIndex={listOrCard === "card" ? 0 : 1}
        />
      </div>

      {/* Horizontal Filters - Always rendered but conditionally visible to maintain state */}
      <div
        className={`mb-6 bg-black/20 rounded-lg border border-white/20 transition-all duration-200 overflow-hidden ${showFilters ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0 border-0"}`}
      >
        <div className="p-4">
          {/* Microphone Count in top right of filter box */}
          <div className="text-right text-sm text-white/50 mb-4">
            Showing {filteredList.length} of {totalCount} microphones
          </div>
          <FilterSortPanel
            microphones={microphones}
            updateFunction={updateMicrophones}
            horizontal={true}
          />
        </div>
      </div>

      {/* Full Width Microphone Grid */}
      <MicrophoneList
        microphones={filteredList}
        listViewStyle={listOrCard}
        fullWidth={true}
        onRegisterMicrophone={onMicrophoneRegister}
      />
    </div>
  );
};

export default CollectionContainer;
